import {
  addOrganizationPolicyRequest,
  deleteAxiosOrganization,
  getAxiosOrganizationById,
  getAxiosOrganizations,
  postAxiosCreateOrganization,
  postAxiosUploadFile,
  putAxiosActivateUser,
  putAxiosOrganization,
  putAxiosUpdateOrgAppSettings,
} from "@API/Organizations/endpoints";
import {
  IAxiosOrganization,
  IGetAxiosOrganizations,
  IPostAxiosOrganization,
  NewOrganizationPayload,
} from "@API/Organizations/types";
import i18n from "@i18n";
import {PayloadAction} from "@reduxjs/toolkit";
import {AxiosError, AxiosResponse, isAxiosError} from "axios";
import {call, put, takeLatest} from "redux-saga/effects";
import {
  addOrganizationPolicy,
  addOrganizationPolicyFail,
  addOrganizationPolicySuccess,
  createOrganizationFail,
  createOrganizationRequest,
  createOrganizationSuccess,
  deleteOrganizationFail,
  deleteOrganizationRequest,
  deleteOrganizationSuccess,
  getOrganizationByIdFail,
  getOrganizationByIdRequest,
  getOrganizationByIdSuccess,
  getOrganizationPolicyFail,
  getOrganizationPolicyRequest,
  getOrganizationPolicySuccess,
  organizationsListFail,
  organizationsListRequest,
  organizationsListSuccess,
  resetCreateOrganizationModal,
  systemUpdateOrganizationFail,
  systemUpdateOrganizationRequest,
  systemUpdateOrganizationSuccess,
  toggleUserActiveStatus,
  toggleUserActiveStatusFail,
  toggleUserActiveStatusSuccess,
  updateOrgAppSettingsFail,
  updateOrgAppSettingsRequest,
  updateOrgAppSettingsSuccess,
  updateOrganizationFail,
  updateOrganizationRequest,
  updateOrganizationSuccess,
  updateSelectedOrganizationId,
  uploadImageOrganizationRequest,
  uploadImageOrganizationSuccess,
} from "./actions";
import {IOrganization, OrganizationId, UploadImageOrganizationPayload,} from "./types";
import {IGetAxiosAppUser} from "@API/AppUser/types";
import {getAxiosAppUser} from "@API/AppUser/endpoints";
import {appUserDataSuccess} from "@Store/AppUser/actions";
import {getOrganizationPolicyEndpoint} from "@API/Policies/endpoints";
import {showSnackbar} from "@Store/toast/actions";
import {
  createOrganizationThemeRequest,
  createOrganizationThemeSuccess,
  createOrganizationThemeFail,
  updateOrganizationThemeRequest,
  updateOrganizationThemeSuccess,
  updateOrganizationThemeFail,
  getOrganizationThemeRequest,
  getOrganizationThemeSuccess,
  getOrganizationThemeFail,
} from "./actions";
import {
  getAxiosTheme,
  postAxiosCreateTheme,
  patchAxiosUpdateTheme,
} from "@API/Organizations/endpoints";
import { CreateThemePayload } from "./types";
import { UpdateThemePayload } from "./types";


function* onOrganizationsListRequest() {
  try {
    const response: IGetAxiosOrganizations = yield call(getAxiosOrganizations);

    if (response.error) throw response.error;

    yield put(organizationsListSuccess(response.data));
  } catch (e) {
    console.error(e);
    yield put(organizationsListFail(e as Error));
  }
}

function* onOrganizationListRequest(action: { payload: string }): Generator {
  try {
    const response = (yield call(
      getAxiosOrganizationById,
      action.payload
    )) as AxiosResponse<IAxiosOrganization>;
    yield put(getOrganizationByIdSuccess(response.data));
  } catch (e) {
    if (e instanceof Error) {
      yield put(getOrganizationByIdFail(e));
    } else {
      yield put(
        getOrganizationByIdFail(new Error("An unexpected error occurred"))
      );
    }
  }
}

function* onUpdateOrganizationRequest({
  payload,
}: PayloadAction<IOrganization>) {
  try {
    const response: IAxiosOrganization = yield call(
      putAxiosOrganization,
      payload
    );
    if (response.error) throw response.error;

    yield new Promise((resolve) => setTimeout(resolve, 500));
    yield put(updateOrganizationSuccess(response.data));
  } catch (e) {
    console.error(e);
    yield put(updateOrganizationFail(e as Error));
  }
}

function* onSystemUpdateOrganizationRequest({
  payload,
}: PayloadAction<IOrganization>) {
  try {
    const response: IAxiosOrganization = yield call(
      putAxiosOrganization,
      payload
    );
    if (response.error) throw response.error;

    yield new Promise((resolve) => setTimeout(resolve, 500));
    yield put(systemUpdateOrganizationSuccess(response.data));
  } catch (e) {
    console.error(e);
    yield put(systemUpdateOrganizationFail(e as Error));
  }
}

function* onUpdateSelectedOrganizationId({
  payload,
}: PayloadAction<OrganizationId>) {
  // yield put(usersListRequest(payload));
}

// system user requests
function* onCreateOrganizationRequest({
  payload,
}: PayloadAction<NewOrganizationPayload>) {
  try {
    const response: IPostAxiosOrganization = yield call(
      postAxiosCreateOrganization,
      payload
    );

    if (response.error) throw response.error;

    yield put(resetCreateOrganizationModal());
    yield put(createOrganizationSuccess(response.data));

    yield put(
      showSnackbar(
        i18n.t("organization.modals.addOrganization.successMessage"),
        "success"
      )
    );
  } catch (e) {
    yield put(createOrganizationFail(e as Error));

    yield put(
      showSnackbar(
        i18n.t("organization.modals.addOrganization.failMessage"),
        "error",
        e
      )
    );
  }
}

function* onDeleteOrganizationRequest({
  payload,
}: PayloadAction<OrganizationId>) {
  try {
    const response: { error?: Error } = yield call(
      // @ts-ignore
      deleteAxiosOrganization,
      payload
    );
    if (response.error) throw response.error;

    yield new Promise((resolve) => setTimeout(resolve, 500));
    yield put(deleteOrganizationSuccess(payload));
  } catch (e) {
    console.error(e);
    yield put(deleteOrganizationFail(e as Error));
  }
}

function* onUploadImageOrganizationRequest({
  payload,
}: PayloadAction<UploadImageOrganizationPayload>) {
  try {
    const { org_id, file_type, file } = payload;

    yield call(postAxiosUploadFile, org_id, file_type, file);

    yield put(uploadImageOrganizationSuccess());

    const appUser: IGetAxiosAppUser = yield call(getAxiosAppUser);
    yield put(appUserDataSuccess(appUser.data));
    yield put(getOrganizationByIdRequest(org_id));
    // notification.success({
    //   message: i18n.t("organization.uploadImage.successMessage"),
    // });

    yield put(
      showSnackbar(i18n.t("organization.uploadImage.successMessage"), "success")
    );
  } catch (e) {
    // If there's an error, dispatch a failure action
    const errorMessage =
      isAxiosError(e) && e.response
        ? e.response.data.message
        : (e as AxiosError).message;
  }
}

function* onAddOrganizationPolicyRequest({ payload }: PayloadAction<any>) {
  try {
    yield call(addOrganizationPolicyRequest, payload);

    yield put(addOrganizationPolicySuccess());
    // notification.success({
    //   message: i18n.t("organization.modals.addPolicy.successMessage"),
    // });

    yield put(
      showSnackbar(
        i18n.t("organization.modals.addPolicy.successMessage"),
        "success"
      )
    );

    yield put(
      getOrganizationPolicyRequest({
        orgId: payload.org_id || "",
        type: payload.type === "PRIVACY" ? "privacy" : "gtac",
      })
    );
  } catch (e) {
    const errorMessage =
      isAxiosError(e) && e.response
        ? e.response.data.message
        : (e as AxiosError).message;

    yield put(addOrganizationPolicyFail());
    // notification.error({
    //   message: i18n.t("organization.modals.addPolicy.failMessage"),
    //   description: errorMessage,
    // });
    yield put(
      showSnackbar(i18n.t("organization.modals.addPolicy.failMessage"), "error", e)
    );
  }
}

function* onToggleUserActiveStatusRequest(
  action: PayloadAction<{ org_id: string; user_id: string; isActive: boolean }>
) {
  try {
    yield call(
      putAxiosActivateUser,
      action.payload.org_id,
      action.payload.user_id,
      action.payload.isActive
    );
    yield put(
      toggleUserActiveStatusSuccess({
        org_id: action.payload.org_id,
        user_id: action.payload.user_id,
        isActive: action.payload.isActive,
      })
    );
    // notification.success({
    //   message: "User status updated successfully"
    // });

    yield put(showSnackbar("User status updated successfully", "success"));
  } catch (e) {
    yield put(toggleUserActiveStatusFail(e as Error));
    yield put(showSnackbar("Failed to update user status", "error", e));
  }
}

function* onGetOrganizationPolicyRequest(action: any) {
  try {
    const { orgId, type } = action.payload;
    const response: AxiosResponse = yield call(
      getOrganizationPolicyEndpoint,
      orgId,
      type
    );
    yield put(getOrganizationPolicySuccess({ orgId, type, ...response.data }));
  } catch (error) {
    yield put(
      getOrganizationPolicyFail({
        orgId: action.payload.orgId,
        type: action.payload.type,
        error,
      })
    );
  }
}

function* onUpdateOrgAppSettingsRequest(action: PayloadAction<{
  orgId: string;
  appSettings: {
    screenshotAllowed: boolean;
    callsDisabled: boolean;
    emailAddressHidden: boolean;
    mobileNumberHidden: boolean;
    favouritesDisabled: boolean;
  };
}>) {
  try {
    const { orgId, appSettings } = action.payload;

    yield call(putAxiosUpdateOrgAppSettings, orgId, appSettings);

    yield put(updateOrgAppSettingsSuccess({
      orgId,
      appSettings
    }));

    // Refresh organization data
    yield put(getOrganizationByIdRequest(orgId));

    yield put(
      showSnackbar("App settings updated successfully", "success")
    );
  } catch (e) {
    yield put(updateOrgAppSettingsFail(e as Error));
    yield put(showSnackbar("Failed to update app settings", "error", e));
  }
}


function* onCreateOrganizationThemeRequest({
  payload,
}: PayloadAction<{ orgId: string; payload: CreateThemePayload }>): Generator {
  try {
    const { orgId, payload: themePayload } = payload;
    const response: any = yield call(postAxiosCreateTheme, orgId, themePayload);
    
    if (response.error) throw response.error;

    yield put(createOrganizationThemeSuccess(response.data));
    
    yield put(
      showSnackbar(
        i18n.t("organization.theme.create.successMessage"),
        "success"
      )
    );
  } catch (e) {
    yield put(createOrganizationThemeFail(e as Error));
    
    yield put(
      showSnackbar(
        i18n.t("organization.theme.create.failMessage"),
        "error",
        e
      )
    );
  }
}



function* onGetOrganizationThemeRequest({
  payload,
}: PayloadAction<string>): Generator {
  try {
    console.log('Saga: Fetching theme for org:', payload);
    const response: any = yield call(getAxiosTheme, payload);
    
    if (response.error) {
      console.log('Saga: No theme found or error:', response.error);
      yield put(getOrganizationThemeFail(response.error));
    } else {
      console.log('Saga: Theme fetched successfully:', response.data);
      
      // Handle if response is array or single object
      let themeData = response.data;
      if (Array.isArray(response.data) && response.data.length > 0) {
        themeData = response.data[0];
      }
      
      yield put(getOrganizationThemeSuccess(themeData));
    }
  } catch (e) {
    console.error('Saga: Error fetching theme:', e);
    yield put(getOrganizationThemeFail(e as Error));
  }
}



function* onUpdateOrganizationThemeRequest({
  payload,
}: PayloadAction<UpdateThemePayload>): Generator {
  try {
    const { orgId, themeId, payload: themePayload } = payload;
    
    console.log('Saga: Making PATCH request:', {
      orgId,
      themeId,
      themePayload
    });
    
    const response: any = yield call(patchAxiosUpdateTheme, orgId, themeId, themePayload);
    
    if (response.error) throw response.error;

    yield put(updateOrganizationThemeSuccess(response.data));
    
    yield put(
      showSnackbar(
        "Theme updated successfully",
        "success"
      )
    );
    
    // Refresh the theme data
    yield put(getOrganizationThemeRequest(orgId));
  } catch (e) {
    yield put(updateOrganizationThemeFail(e as Error));
    
    yield put(
      showSnackbar(
        "Failed to update theme",
        "error",
        e
      )
    );
  }
}

function* onUpdateTheme({
  payload
}: PayloadAction<UpdateThemePayload>) {
  // destructure like this:
  const { orgId, themeId, payload: themePayload } = payload;
  // ...
}



export default function* () {
  yield takeLatest(organizationsListRequest, onOrganizationsListRequest);
  yield takeLatest(getOrganizationByIdRequest, onOrganizationListRequest);
  yield takeLatest(
    updateSelectedOrganizationId,
    onUpdateSelectedOrganizationId
  );
  yield takeLatest(updateOrganizationRequest, onUpdateOrganizationRequest);
  yield takeLatest(createOrganizationRequest, onCreateOrganizationRequest);
  yield takeLatest(deleteOrganizationRequest, onDeleteOrganizationRequest);
  yield takeLatest(
    uploadImageOrganizationRequest,
    onUploadImageOrganizationRequest
  );
  yield takeLatest(addOrganizationPolicy, onAddOrganizationPolicyRequest);
  yield takeLatest(toggleUserActiveStatus, onToggleUserActiveStatusRequest);
  yield takeLatest(
    systemUpdateOrganizationRequest,
    onSystemUpdateOrganizationRequest
  );
  yield takeLatest(
    getOrganizationPolicyRequest.type,
    onGetOrganizationPolicyRequest
  );
  yield takeLatest(
    updateOrgAppSettingsRequest,
    onUpdateOrgAppSettingsRequest
  );
  yield takeLatest(createOrganizationThemeRequest, onCreateOrganizationThemeRequest);
  yield takeLatest(updateOrganizationThemeRequest, onUpdateOrganizationThemeRequest);
  yield takeLatest(getOrganizationThemeRequest, onGetOrganizationThemeRequest);
}


